/**
 * Agreement Service
 * 
 * Handles all agreement-related business logic including:
 * - Agreement creation and renewal
 * - Expiration tracking and notifications
 * - Cancellation and suspension workflows
 * - Payment processing coordination
 * - Auto-renewal management
 * - Agreement reporting and analytics
 * 
 * @example
 * ```typescript
 * const agreementService = new AgreementService();
 * 
 * // Create a new agreement
 * const agreement = await agreementService.createAgreement({
 *   customer_id: 'customer-123',
 *   agreement_type: 'membership',
 *   monthly_premium: 49.99,
 *   start_date: '2025-01-01'
 * });
 * 
 * // Find expiring agreements
 * const expiring = await agreementService.getExpiringAgreements(30);
 * 
 * // Cancel agreement
 * await agreementService.cancelAgreement('agreement-123', 'Customer request');
 * ```
 */

import { BaseService, ServiceError, ValidationSchema, CommonSchemas } from './BaseService';
import { repositories } from '../repositories';
import { Agreement, AgreementFilters } from '../repositories/AgreementRepository';
import { QueryOptions } from '../repositories/BaseRepository';

/**
 * Input for creating a new agreement
 */
export interface CreateAgreementInput {
  customer_id: string;
  agreement_number?: string;
  agreement_type: 'membership' | 'warranty' | 'service_contract';
  status?: 'active' | 'pending' | 'cancelled' | 'suspended' | 'expired';
  
  // Dates
  start_date: string;
  end_date?: string;
  renewal_date?: string;
  
  // Pricing
  premium_amount: number;
  
  // Terms
  billing_frequency?: 'monthly' | 'quarterly' | 'annual';
  auto_renew?: boolean;
  payment_method?: string;
  
  // Coverage
  coverage_details?: Record<string, any>;
  equipment_ids?: string[];
  
  // Contract details
  term_months?: number;
  plan_id?: string;
  product_id?: string;
}

/**
 * Input for updating an agreement
 */
export interface UpdateAgreementInput {
  status?: Agreement['status'];
  premium_amount?: number;
  auto_renew?: boolean;
  renewal_date?: string;
  end_date?: string;
  coverage_details?: Record<string, any>;
  equipment_ids?: string[];
  billing_frequency?: 'monthly' | 'quarterly' | 'annual';
  payment_method?: string;
}

/**
 * Agreement renewal input
 */
export interface RenewAgreementInput {
  new_start_date: string;
  new_end_date?: string;
  premium_amount?: number;
}

/**
 * Agreement statistics with revenue
 */
export interface AgreementRevenue {
  total_agreements: number;
  active_agreements: number;
  monthly_recurring_revenue: number;
  annual_recurring_revenue: number;
  average_premium: number;
  expiring_this_month: number;
  expiring_next_month: number;
  cancellation_rate: number;
  renewal_rate: number;
}

/**
 * Agreement Service Class
 */
export class AgreementService extends BaseService {
  /**
   * Validation schema for creating agreements
   */
  private createAgreementSchema: ValidationSchema = {
    customer_id: {
      required: true,
      type: 'string',
      message: 'Customer ID is required'
    },
    agreement_type: {
      required: true,
      type: 'string',
      message: 'Agreement type is required'
    },
    start_date: {
      required: true,
      type: 'date',
      message: 'Start date is required and must be valid'
    },
    end_date: {
      type: 'date',
      message: 'End date must be a valid date'
    },
    premium_amount: {
      ...CommonSchemas.currency,
      required: true,
      message: 'Premium amount is required'
    }
  };

  /**
   * Create a new agreement
   */
  async createAgreement(input: CreateAgreementInput): Promise<Agreement> {
    try {
      // Validate input
      this.validate(input, this.createAgreementSchema);

      // Validate customer exists
      const customer = await repositories.customers.findById(input.customer_id);
      if (!customer) {
        throw ServiceError.notFound('Customer', input.customer_id);
      }

      // Validate dates
      const startDate = new Date(input.start_date);
      if (input.end_date) {
        const endDate = new Date(input.end_date);
        if (endDate <= startDate) {
          throw ServiceError.validation([{
            field: 'end_date',
            message: 'End date must be after start date'
          }]);
        }
      }

      // Generate agreement number if not provided
      if (!input.agreement_number) {
        input.agreement_number = await this.generateAgreementNumber();
      }

      // Calculate end date if not provided (default 1 year)
      let agreementData: any = { ...input };
      if (!agreementData.end_date && agreementData.term_months) {
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + agreementData.term_months);
        agreementData.end_date = endDate.toISOString();
      } else if (!agreementData.end_date) {
        const endDate = new Date(startDate);
        endDate.setFullYear(endDate.getFullYear() + 1);
        agreementData.end_date = endDate.toISOString();
      }

      // Set default status
      if (!agreementData.status) {
        agreementData.status = 'active';
      }

      // Set default auto-renew
      if (agreementData.auto_renew === undefined) {
        agreementData.auto_renew = true;
      }

      // Create agreement
      const agreement = await repositories.agreements.create(agreementData);

      // Emit event
      await this.emit('agreement.created', {
        agreementId: agreement.id,
        customerId: agreement.customer_id,
        agreement
      });

      return agreement;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update an existing agreement
   */
  async updateAgreement(
    agreementId: string,
    input: UpdateAgreementInput
  ): Promise<Agreement> {
    try {
      // Validate input
      this.validate(input, {
        premium_amount: CommonSchemas.currency
      });

      // Check if agreement exists
      const existing = await repositories.agreements.findById(agreementId);
      if (!existing) {
        throw ServiceError.notFound('Agreement', agreementId);
      }

      // Update agreement
      const agreement = await repositories.agreements.update(agreementId, input);

      // Emit event
      await this.emit('agreement.updated', {
        agreementId: agreement.id,
        customerId: agreement.customer_id,
        agreement,
        changes: input
      });

      return agreement;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get agreement by ID
   */
  async getAgreement(agreementId: string): Promise<Agreement> {
    try {
      const agreement = await repositories.agreements.findById(agreementId);
      
      if (!agreement) {
        throw ServiceError.notFound('Agreement', agreementId);
      }

      return agreement;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get agreements by filters
   */
  async getAgreements(
    filters?: AgreementFilters,
    options?: QueryOptions
  ): Promise<Agreement[]> {
    try {
      return await repositories.agreements.findByFilters(filters || {}, options);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get agreements by customer
   */
  async getCustomerAgreements(
    customerId: string,
    includeInactive: boolean = false
  ): Promise<Agreement[]> {
    try {
      const filters: AgreementFilters = { customerId };
      
      if (!includeInactive) {
        filters.status = 'active';
      }

      return await repositories.agreements.findByCustomer(customerId);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get expiring agreements
   */
  async getExpiringAgreements(days: number = 30): Promise<Agreement[]> {
    try {
      return await repositories.agreements.findExpiringSoon(days);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Renew an agreement
   */
  async renewAgreement(
    agreementId: string,
    input: RenewAgreementInput
  ): Promise<Agreement> {
    try {
      // Validate input
      this.validate(input, {
        new_start_date: {
          required: true,
          type: 'date',
          message: 'New start date is required'
        },
        new_end_date: {
          type: 'date',
          message: 'End date must be valid'
        },
        premium_amount: CommonSchemas.currency
      });

      // Get existing agreement
      const existing = await this.getAgreement(agreementId);

      // Calculate new end date if not provided
      let newEndDate = input.new_end_date;
      if (!newEndDate) {
        const startDate = new Date(input.new_start_date);
        const termMonths = existing.term_months || 12;
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + termMonths);
        newEndDate = endDate.toISOString();
      }

      // Update agreement with renewal
      const renewed = await this.updateAgreement(agreementId, {
        status: 'active',
        ...input,
        end_date: newEndDate
      });

      // Emit renewal event
      await this.emit('agreement.renewed', {
        agreementId: renewed.id,
        customerId: renewed.customer_id,
        agreement: renewed,
        oldEndDate: existing.end_date,
        newEndDate: newEndDate
      });

      return renewed;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Cancel an agreement
   */
  async cancelAgreement(
    agreementId: string,
    reason: string,
    cancelledBy: string
  ): Promise<Agreement> {
    try {
      // Get agreement
      const agreement = await this.getAgreement(agreementId);

      // Validate can be cancelled
      if (agreement.status === 'cancelled') {
        throw ServiceError.conflict('Agreement is already cancelled');
      }

      // Cancel using repository method
      const cancelled = await repositories.agreements.cancel(agreementId, cancelledBy);

      // Emit event
      await this.emit('agreement.cancelled', {
        agreementId: cancelled.id,
        customerId: cancelled.customer_id,
        agreement: cancelled,
        reason,
        cancelledBy
      });

      return cancelled;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Suspend an agreement
   */
  async suspendAgreement(
    agreementId: string,
    reason: string,
    suspendedBy: string
  ): Promise<Agreement> {
    try {
      // Get agreement
      const agreement = await this.getAgreement(agreementId);

      // Validate can be suspended
      if (agreement.status !== 'active') {
        throw ServiceError.conflict('Only active agreements can be suspended');
      }

      // Suspend using repository method
      const suspended = await repositories.agreements.suspend(agreementId, suspendedBy);

      // Emit event
      await this.emit('agreement.suspended', {
        agreementId: suspended.id,
        customerId: suspended.customer_id,
        agreement: suspended,
        reason,
        suspendedBy
      });

      return suspended;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Reactivate a suspended agreement
   */
  async reactivateAgreement(
    agreementId: string,
    reactivatedBy: string
  ): Promise<Agreement> {
    try {
      // Get agreement
      const agreement = await this.getAgreement(agreementId);

      // Validate can be reactivated
      if (agreement.status !== 'suspended') {
        throw ServiceError.conflict('Only suspended agreements can be reactivated');
      }

      // Reactivate using repository method
      const reactivated = await repositories.agreements.reactivate(agreementId, reactivatedBy);

      // Emit event
      await this.emit('agreement.reactivated', {
        agreementId: reactivated.id,
        customerId: reactivated.customer_id,
        agreement: reactivated,
        reactivatedBy
      });

      return reactivated;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get agreement statistics and revenue
   */
  async getRevenue(): Promise<AgreementRevenue> {
    try {
      const stats = await repositories.agreements.getStatistics();
      
      // Calculate additional metrics
      const allAgreements = await repositories.agreements.findAll();
      const activeAgreements = allAgreements.filter(a => a.status === 'active');
      
      const totalRevenue = activeAgreements.reduce(
        (sum, a) => sum + (a.premium_amount || 0),
        0
      );

      return {
        total_agreements: stats.total,
        active_agreements: activeAgreements.length,
        monthly_recurring_revenue: totalRevenue, // TODO: Calculate based on billing_frequency
        annual_recurring_revenue: totalRevenue * 12, // TODO: Calculate based on billing_frequency
        average_premium: stats.averagePremium,
        expiring_this_month: stats.expiringNextMonth,
        expiring_next_month: 0, // TODO: Calculate
        cancellation_rate: 0, // TODO: Calculate from history
        renewal_rate: 0 // TODO: Calculate from history
      };
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Process auto-renewals (called by cron job)
   */
  async processAutoRenewals(): Promise<{ renewed: number; failed: number }> {
    try {
      let renewed = 0;
      let failed = 0;

      // Find agreements expiring in next 7 days with auto-renew enabled
      const expiring = await repositories.agreements.findExpiringSoon(7);
      const autoRenewable = expiring.filter(a => a.auto_renew && a.status === 'active');

      for (const agreement of autoRenewable) {
        try {
          const newStartDate = new Date(agreement.end_date!);
          newStartDate.setDate(newStartDate.getDate() + 1);

          await this.renewAgreement(agreement.id, {
            new_start_date: newStartDate.toISOString(),
            premium_amount: agreement.premium_amount
          });

          renewed++;
        } catch (error) {
          console.error(`Failed to auto-renew agreement ${agreement.id}:`, error);
          failed++;
          
          // Emit failure event
          await this.emit('agreement.renewal.failed', {
            agreementId: agreement.id,
            customerId: agreement.customer_id,
            agreement,
            error
          });
        }
      }

      return { renewed, failed };
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Generate unique agreement number
   */
  private async generateAgreementNumber(): Promise<string> {
    const timestamp = Date.now().toString().substring(-8);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `AGR-${timestamp}-${random}`;
  }
}

/**
 * Singleton instance
 */
export const agreementService = new AgreementService();
